import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Layout from '../../components/Layout';
import Card from '@/components/Card';
import Breadcrumb from '@/components/Breadcrumb';
import AlertDialog from '@/components/AlertDialog';
import api from '@/services/api';
import type { SelectedFilters } from '@/components/Filters/FiltersDisplay';
import { ListStack, CardStack } from './styles';
import SearchPanel, { type DisplayMode } from './SearchPanel';
import ResultsGrid from './ResultsGrid';
import ResultsTable from './ResultsTable';

const TABLE_PAGE_SIZES = [10, 25, 50, 100];

// Turns the selected filters into /devices/list query params
function toDeviceQuery(filters: SelectedFilters) {
  const { online, inUse, ...rest } = filters;
  return {
    ...rest,
    ...(online ? { online: online === 'online' } : {}),
    ...(inUse ? { inUse: inUse === 'enabled' } : {}),
  };
}

function ListEquipments() {
  const [filters, setFilters] = useState<SelectedFilters>({});
  const [devices, setDevices] = useState<DeviceType[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    isLg ? 'list' : 'grid'
  );
  const [devicesInUse, setDevicesInUse] = useState<
    Record<string | number, boolean>
  >({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(TABLE_PAGE_SIZES[0]);
  const [gridPageSize, setGridPageSize] = useState(0);
  const pageSize = displayMode === 'list' ? tablePageSize : gridPageSize;

  // Bulk mode state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkAction, setBulkAction] = useState<'enable' | 'disable' | null>(
    null
  );
  const [stagedInUse, setStagedInUse] = useState<
    Record<string | number, boolean>
  >({});
  const [bulkSaveDialogOpen, setBulkSaveDialogOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch the page of devices matching the applied filters
  useEffect(() => {
    // The grid reports its page size once it has measured its columns
    if (!pageSize) return;
    // Drops the response of a request superseded by a newer one
    let ignore = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get<PageResponse<DeviceType>>(
          '/devices/list',
          {
            params: { ...toDeviceQuery(filters), page, size: pageSize },
            // Arrays as repeated keys (manufacturer=a&manufacturer=b)
            paramsSerializer: { indexes: null },
          }
        );
        if (ignore) return;
        const { content, totalElements: total } = response.data;
        setDevices(content);
        setTotalElements(total);

        // Merged, so in-use changes made on other pages are kept
        setDevicesInUse((prev) => {
          const next = { ...prev };
          content.forEach((device) => {
            if (device.inUse !== undefined && device.inUse !== null) {
              next[device.id] = device.inUse;
            }
          });
          return next;
        });
      } catch (error) {
        if (!ignore) console.error(t('common.error'), error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();
    return () => {
      ignore = true;
    };
  }, [filters, page, pageSize, t]);

  // Filter options, which don't depend on the search
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [dashboardRes, modelsRes] = await Promise.all([
          api.get<DashboardResponse>('/dashboard'),
          api.get<ModelType[]>('/models'),
        ]);
        setManufacturers(
          dashboardRes.data.manufacturerCount
            .map((item) => item.manufacturer)
            .sort()
        );
        setModels(
          modelsRes.data.map((model) => model.alias || model.key).sort()
        );
      } catch (error) {
        console.error(t('common.error'), error);
      }
    };

    fetchOptions();
  }, [t]);

  // Every new search starts on the first page
  const applyFilters = useCallback((value: SetStateAction<SelectedFilters>) => {
    setPage(0);
    setFilters(value);
  }, []);

  const handleTablePageSizeChange = useCallback((size: number) => {
    setPage(0);
    setTablePageSize(size);
  }, []);

  const handleGridPageSizeChange = useCallback((size: number) => {
    setPage(0);
    setGridPageSize(size);
  }, []);

  // On mobile: list view and bulk actions aren't supported, so fall back to
  // card view and drop out of any active bulk edition
  useEffect(() => {
    if (!isMobile) return;
    setPage(0);
    setDisplayMode('grid');
    setBulkMode(false);
    setBulkAction(null);
    setStagedInUse({});
  }, [isMobile]);

  // Handlers
  const handleDisplayModeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: DisplayMode | null) => {
      if (newMode !== null && !(isMobile && newMode === 'list')) {
        setPage(0);
        setDisplayMode(newMode);
      }
    },
    [isMobile]
  );

  const handleUseModeChange = useCallback(
    (deviceId: string | number, newInUseStatus: boolean | null) => {
      if (newInUseStatus !== null) {
        setDevicesInUse((prevState) => ({
          ...prevState,
          [deviceId]: newInUseStatus,
        }));
      }
    },
    []
  );

  const handleCardClick = useCallback(
    (id: number) => {
      navigate(`/equipment/${id}`);
    },
    [navigate]
  );

  /** BULK MODE HANDLERS **/

  // handle that enable bulk mode with chosen action
  const handleBulkAction = useCallback(
    (action: 'enable' | 'disable') => {
      if (isMobile) return;
      setBulkAction(action);
      setStagedInUse({});
      setBulkMode(true);
    },
    [isMobile]
  );

  // handle that toggle individual device selection
  const handleDeviceSelect = useCallback(
    (deviceId: string | number) => {
      setStagedInUse((prev) => {
        const next = { ...prev };
        if (deviceId in next) {
          delete next[deviceId];
        } else {
          next[deviceId] = bulkAction === 'enable';
        }
        return next;
      });
    },
    [bulkAction]
  );

  // handle that select all devices of the current page with the current bulk action
  const handleSelectAll = useCallback(() => {
    const newStaged: Record<string | number, boolean> = {};
    const inUseValue = bulkAction === 'enable';
    devices.forEach((device) => {
      newStaged[device.id] = inUseValue;
    });
    setStagedInUse(newStaged);
  }, [devices, bulkAction]);

  // handle that unmark all staged devices
  const handleDeselectAll = useCallback(() => {
    setStagedInUse({});
  }, []);

  // handle that cancel bulk mode and clear staging
  const handleBulkCancel = useCallback(() => {
    setBulkMode(false);
    setBulkAction(null);
    setStagedInUse({});
  }, []);

  // handle that open dialog to confirm bulk action
  const handleBulkSaveRequest = useCallback(() => {
    setBulkSaveDialogOpen(true);
  }, []);

  // handle that send all staged changes to the API, then update local state and exit bulk mode
  const handleBulkSaveConfirm = useCallback(async () => {
    try {
      const enableIds: (string | number)[] = [];
      const disableIds: (string | number)[] = [];

      Object.entries(stagedInUse).forEach(([id, value]) => {
        const numericId = Number(id);
        const deviceId = isNaN(numericId) ? id : numericId;
        if (value) {
          enableIds.push(deviceId);
        } else {
          disableIds.push(deviceId);
        }
      });

      // sent to separate routes by action
      if (enableIds.length > 0) {
        await api.patch('/devices/enable', enableIds);
      }
      if (disableIds.length > 0) {
        await api.patch('/devices/disable', disableIds);
      }

      // apply changes on local state
      setDevicesInUse((prev) => ({
        ...prev,
        ...stagedInUse,
      }));

      setBulkMode(false);
      setStagedInUse({});
    } catch (error) {
      console.error(t('common.error'), error);
    }
  }, [stagedInUse, t]);

  // count devices on staging area to show in confirmation dialog
  const stagedCount = useMemo(
    () => Object.keys(stagedInUse).length,
    [stagedInUse]
  );

  return (
    <Layout noLeftMargin>
      <ListStack>
        <Breadcrumb
          paths={[
            { label: t('listEquipments.dashboard'), route: '/' },
            { label: t('listEquipments.equipmentList'), route: '/list' },
          ]}
        />
        <CardStack>
          <Card>
            <SearchPanel
              filters={filters}
              setFilters={applyFilters}
              manufacturers={manufacturers}
              models={models}
              displayMode={displayMode}
              onDisplayModeChange={handleDisplayModeChange}
              pageSize={tablePageSize}
              pageSizeOptions={TABLE_PAGE_SIZES}
              onPageSizeChange={handleTablePageSizeChange}
              bulkMode={bulkMode}
              bulkAction={bulkAction}
              onBulkAction={handleBulkAction}
              onBulkCancel={handleBulkCancel}
              onBulkSave={handleBulkSaveRequest}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              stagedCount={stagedCount}
              isMobile={isMobile}
            />
          </Card>

          {displayMode === 'list' ? (
            <Card>
              <div style={{ width: '100%' }}>
                <ResultsTable
                  loading={loading}
                  data={devices}
                  totalCount={totalElements}
                  rowsPerPage={tablePageSize}
                  page={page}
                  setPage={setPage}
                  onDisplayModeChange={handleDisplayModeChange}
                  devicesInUse={devicesInUse}
                  onUseModeChange={handleUseModeChange}
                  bulkMode={bulkMode}
                  bulkAction={bulkAction}
                  stagedInUse={stagedInUse}
                  onDeviceSelect={handleDeviceSelect}
                />
              </div>
            </Card>
          ) : (
            <div style={{ width: '100%' }}>
              <ResultsGrid
                loading={loading}
                data={devices}
                totalCount={totalElements}
                page={page}
                setPage={setPage}
                onPageSizeChange={handleGridPageSizeChange}
                handleCardClick={handleCardClick}
              />
            </div>
          )}
        </CardStack>
      </ListStack>

      {/** Confirmation dialog to confirm save bulk actions */}
      <AlertDialog
        title={t('common.confirmAction', 'Confirmar alteração em massa')}
        content={
          <Typography>
            {t(
              'listEquipments.confirmBulkSave',
              `Deseja realmente aplicar as alterações de "Em Uso" para ${stagedCount} dispositivo(s)?`,
              { count: stagedCount }
            )}
          </Typography>
        }
        open={bulkSaveDialogOpen}
        setOpen={setBulkSaveDialogOpen}
        setValue={handleBulkSaveConfirm}
        confirmText={t('common.save', 'Salvar')}
        cancelText={t('common.cancel', 'Cancelar')}
      />
    </Layout>
  );
}

export default ListEquipments;
